import React, { Component } from 'react'
import { Alert, Badge, Button, Card, Checkbox, Empty, Input, Modal, Progress, Radio, Result, Select, Space, Spin, Table, Tag, Tooltip, Typography, message } from 'antd'
import { BulbOutlined, CheckCircleOutlined, CloudServerOutlined, DatabaseOutlined, DeleteOutlined, ReloadOutlined, SendOutlined, TeamOutlined } from '@ant-design/icons'
import { withRouter } from 'react-router-dom'
import {
    applyVibeProjectPermission,
    approveVibeProjectPermissionRequest,
    getAllVibeProjectList,
    getMyVibeProjectPermissionRequestCount,
    getMyVibeProjectPermissionRequestList,
    getVibeProjectAuthorization,
    queryApplicableVibeProjects,
    recommendVibeProjects,
    saveVibeProjectAuthorization,
} from '../../request'
import {
    buildProjectAuthorizationGrants,
    getAicDataStatusMeta,
    getDatasetPolicyMeta,
    isSystemManagedDatasetGrant,
} from './vibeDataset'
import {
    DEFAULT_PROJECT_KIND_FILTER,
    filterVibeProjects,
    getVibeProjectKind,
} from './vibeProjectFilter'
import {
    BATCH_APPLY_PAGE_SIZE,
    filterBatchApplyProjects,
    filterBatchApplyProjectsByKind,
    getBatchApplyPage,
} from './vibeProjectBatchApply'
import VibeCodexGuideDialog from '../vibe/VibeCodexGuideDialog'
import VibeProjectDeleteDialog from './VibeProjectDeleteDialog'
import VibeUploadAuthorizerDialog from './VibeUploadAuthorizerDialog'
import { canDeleteVibeProject } from './vibeProjectDelete'
import { VIBE_CODEX_GUIDE_SCENE } from '../vibe/vibeCodexGuide'
import { confirmAuthorizationDocumentationWarnings } from '../vibe/vibeAuthorizationWarning'

const { Text } = Typography

// 提交前按 id 回查记录时单批最多带多少条 id（分批查，避免一次带太多或漏查）
const BATCH_APPLY_ID_LOOKUP_SIZE = 100
// "全选全部"在后端仍分页时的兜底翻页参数
const BATCH_APPLY_SELECT_ALL_PAGE_SIZE = 100
const BATCH_APPLY_SELECT_ALL_MAX_PAGES = 100

const DEPLOYMENT_MODE_META = {
    server: { text: '服务器部署', color: 'blue' },
    local: { text: '仅本地运行', color: 'default' },
}

const PROJECT_KIND_META = {
    application: { text: 'Vibe项目', color: 'blue' },
    dataset: { text: 'AIC项目', color: 'cyan' },
}

const BUSINESS_SUBJECT_META = {
    lechun: { text: '乐纯', color: 'green' },
    superbody: { text: '超体', color: 'orange' },
}

const STATUS_META = {
    approved: { text: '已创建', color: 'green' },
    provisioning: { text: '接入中', color: 'processing' },
    ready: { text: '已就绪', color: 'green' },
    failed: { text: '接入失败', color: 'red' },
    disabled: { text: '已停用', color: 'default' },
}

const UPDATE_FREQUENCY_META = {
    daily: '每日',
    weekly: '每周',
    monthly: '每月',
    quarterly: '每季度',
    irregular: '不定期',
}

class VibeProjectList extends Component {
    state = {
        loading: true,
        loadError: false,
        projects: [],
        projectFilter: 'all',
        projectKindFilter: DEFAULT_PROJECT_KIND_FILTER,
        listPage: 1,
        registrantFilter: [],
        aiRequirement: '',
        aiLoading: false,
        aiResult: null,
        actionProjectId: null,
        actionType: null,
        applyVisible: false,
        applyProject: null,
        applyPermissionScope: 'database',
        applyDatabasePermissionType: 'readonly',
        applying: false,
        batchApplyVisible: false,
        batchApplyProjects: [],
        batchApplyRows: [],
        batchApplyTotal: 0,
        batchApplyServerPaged: false,
        batchApplyTableLoading: false,
        batchApplyRecordById: {},
        // 默认不选中，用户勾选哪些就申请哪些（翻页/筛选后仍保留已勾选项）
        selectedBatchProjectIds: [],
        // "全选全部"拉取跨页全量项目时的 loading
        batchApplySelectAllLoading: false,
        batchDatabasePermissionType: 'readonly',
        batchApplyKeyword: '',
        batchApplyKindFilter: DEFAULT_PROJECT_KIND_FILTER,
        batchApplyOwnerFilter: [],
        batchApplyOnlySelected: false,
        batchApplyPage: 1,
        batchApplying: false,
        batchApplyProgress: null,
        codexGuideVisible: false,
        codexGuideScene: VIBE_CODEX_GUIDE_SCENE.NORMAL,
        authorizationVisible: false,
        authorizationLoading: false,
        authorizationLoadError: false,
        authorizationSaving: false,
        authorizationProjectId: null,
        authorizationProject: null,
        authorizationUsers: [],
        selectedAuthorizationUserIds: [],
        authorizationPermissionTypes: {},
        authorizationKeyword: '',
        uploadAuthorizerVisible: false,
        uploadAuthorizerProject: null,
        pendingRequestCount: 0,
        requestCountLoading: false,
        requestVisible: false,
        requestLoading: false,
        requestLoadError: false,
        permissionRequests: [],
        approvingRequestId: null,
        selectedRequestIds: [],
        batchApproving: false,
        focusedRequestId: null,
        deleteVisible: false,
        deleteProject: null,
    }

    componentDidMount() {
        const authorizeProjectId = this.getLinkedAuthorizationProjectId()
        this.loadProjects().then(projects => {
            if (!authorizeProjectId) return
            const project = projects.find(item => String(item.id) === String(authorizeProjectId))
            this.clearLinkedAuthorizationProjectId()
            if (project && this.isTrue(project.isOwner)) {
                this.openAuthorization(project)
            }
        })
        this.loadPendingRequestCount()
        const requestId = this.getLinkedRequestId()
        if (requestId) {
            this.setState({
                requestVisible: true,
                focusedRequestId: requestId,
            }, this.loadPermissionRequests)
        }
    }

    componentWillUnmount() {
        if (this.batchApplyKeywordTimer) {
            clearTimeout(this.batchApplyKeywordTimer)
            this.batchApplyKeywordTimer = null
        }
    }

    getLinkedRequestId = () => {
        const search = new URLSearchParams(this.props.location?.search)
        return search.get('requestId')
    }

    getLinkedAuthorizationProjectId = () => {
        const search = new URLSearchParams(this.props.location?.search)
        return search.get('authorizeProjectId')
    }

    clearLinkedAuthorizationProjectId = () => {
        const { history, location } = this.props
        const search = new URLSearchParams(location?.search)
        search.delete('authorizeProjectId')
        history.replace({
            pathname: location.pathname,
            search: search.toString(),
        })
    }

    loadProjects = () => {
        this.setState({ loading: true, loadError: false })
        return getAllVibeProjectList({})
            .then(projects => {
                const latestProjects = Array.isArray(projects) ? projects : []
                const projectById = new Map(latestProjects.map(project => [String(project.id), project]))
                this.setState(prevState => ({
                    projects: latestProjects,
                    loading: false,
                    aiResult: prevState.aiResult ? {
                        ...prevState.aiResult,
                        recommendations: (prevState.aiResult.recommendations || []).map(recommendation => ({
                            ...recommendation,
                            ...(projectById.get(String(recommendation.id)) || {}),
                        })),
                        } : null,
                }))
                return latestProjects
            })
            .catch(() => {
                this.setState({ loading: false, loadError: true })
                return []
            })
    }

    loadPendingRequestCount = () => {
        this.setState({ requestCountLoading: true })
        getMyVibeProjectPermissionRequestCount({})
            .then(res => this.setState({
                pendingRequestCount: Number(res?.pendingCount) || 0,
            }))
            .catch(() => {})
            .finally(() => this.setState({ requestCountLoading: false }))
    }

    refreshPage = () => {
        this.loadProjects()
        this.loadPendingRequestCount()
    }

    recommendProjects = () => {
        const requirement = this.state.aiRequirement.trim()
        if (!requirement) {
            message.warning('请先描述你需要什么数据')
            return
        }
        this.setState({ aiLoading: true, aiResult: null })
        recommendVibeProjects({ requirement })
            .then(result => this.setState({
                aiResult: {
                    answer: result?.answer || '',
                    recommendations: Array.isArray(result?.recommendations) ? result.recommendations : [],
                },
            }))
            .catch(() => {})
            .finally(() => this.setState({ aiLoading: false }))
    }

    isTrue = value => value === true || value === 1 || value === '1' || value === 'true'

    getUpdateFrequencyText = record => {
        try {
            const config = typeof record.updateFrequencyConfig === 'string'
                ? JSON.parse(record.updateFrequencyConfig)
                : record.updateFrequencyConfig
            if (config?.sourceText) return config.sourceText
        } catch (error) {
            // Fall back to the normalized frequency when legacy config is not valid JSON.
        }
        return UPDATE_FREQUENCY_META[record.updateFrequency] || record.updateFrequency || '-'
    }

    isDatasetProject = project => getVibeProjectKind(project) === 'dataset'

    getRegistrantOptions = projects => {
        const registrants = new Map()
        projects.forEach(project => {
            if (project.ownerUserId === undefined || project.ownerUserId === null || project.ownerUserId === '') return
            const value = String(project.ownerUserId)
            if (registrants.has(value)) return
            const nick = project.ownerUserNick || ''
            const name = project.ownerUserName || ''
            registrants.set(value, {
                value,
                label: nick && name && nick !== name ? `${nick}（${name}）` : (nick || name || value),
            })
        })
        return Array.from(registrants.values()).sort((left, right) => (
            left.label.localeCompare(right.label, 'zh-CN')
        ))
    }

    filterProjectsByRegistrant = (projects, registrantFilter) => {
        if (!registrantFilter.length) return projects
        const registrantIds = new Set(registrantFilter.map(String))
        return projects.filter(project => registrantIds.has(String(project.ownerUserId)))
    }

    filterProjects = (projects, projectFilter, projectKindFilter) => (
        filterVibeProjects(projects, projectFilter, projectKindFilter)
    )

    // 主列表筛选条件变化时回到第一页，刷新时保留当前页。
    updateListFilter = patch => this.setState({ listPage: 1, ...patch })

    getEmptyDescription = (projectFilter, projectKindFilter) => {
        const kindText = projectKindFilter === 'dataset'
            ? 'AIC项目'
            : (projectKindFilter === 'application' ? 'Vibe项目' : '项目')
        if (projectFilter === 'owned') return `暂无我创建的${kindText}`
        if (projectFilter === 'permitted') return `暂无我有权限的别人${kindText}`
        return `暂无${kindText}`
    }

    formatDate = value => {
        if (!value) return '-'
        const date = new Date(value)
        if (Number.isNaN(date.getTime())) return String(value)
        const pad = number => (number < 10 ? `0${number}` : String(number))
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
    }

    renderStatus = status => {
        const meta = STATUS_META[(status || '').toLowerCase()] || { text: status || '-', color: 'blue' }
        return <Tag color={meta.color}>{meta.text}</Tag>
    }

    renderAicDataStatus = (_, record) => {
        if (!this.isDatasetProject(record)) return <Text type="secondary">-</Text>
        const meta = getAicDataStatusMeta(record.lastSuccessfulUploadAt)
        return (
            <Space direction="vertical" size={0}>
                <Tag color={meta.color}>{meta.text}</Tag>
                {meta.uploaded && (
                    <Text type="secondary">{this.formatDate(record.lastSuccessfulUploadAt)}</Text>
                )}
            </Space>
        )
    }

    renderDeploymentMode = mode => {
        const meta = DEPLOYMENT_MODE_META[mode] || { text: mode || '-', color: 'default' }
        return <Tag color={meta.color}>{meta.text}</Tag>
    }

    renderProjectKind = kind => {
        const meta = PROJECT_KIND_META[kind] || PROJECT_KIND_META.application
        return <Tag color={meta.color}>{meta.text}</Tag>
    }

    renderBusinessSubject = subject => {
        const meta = BUSINESS_SUBJECT_META[subject] || { text: subject || '-', color: 'default' }
        return <Tag color={meta.color}>{meta.text}</Tag>
    }

    openAuthorization = record => {
        const projectId = record.id
        this.setState({
            actionProjectId: projectId,
            actionType: 'authorize',
            authorizationVisible: true,
            authorizationLoading: true,
            authorizationLoadError: false,
            authorizationProjectId: projectId,
            authorizationProject: record,
            authorizationUsers: [],
            selectedAuthorizationUserIds: [],
            authorizationPermissionTypes: {},
            authorizationKeyword: '',
        })
        getVibeProjectAuthorization({ projectId })
            .then(res => {
                const users = Array.isArray(res?.accounts) ? res.accounts : []
                const authorizationProject = { ...record, ...(res || {}) }
                // 2026-07-31 coder(lq): 新授权默认只读，已有授权回显当前权限，项目所有者固定读写。
                const authorizationPermissionTypes = users.reduce((result, item) => {
                    result[String(item.userId)] = this.isTrue(item.isOwner) || item.permissionType === 'readwrite'
                        ? 'readwrite'
                        : 'readonly'
                    return result
                }, {})
                this.setState({
                    authorizationProject,
                    authorizationUsers: users,
                    authorizationPermissionTypes,
                    selectedAuthorizationUserIds: users
                        .filter(item => (
                            this.isTrue(item.authorized)
                            || this.isTrue(item.isOwner)
                            || (this.isDatasetProject(authorizationProject)
                                && isSystemManagedDatasetGrant(item, true))
                        ))
                        .map(item => String(item.userId)),
                })
            })
            .catch(() => this.setState({ authorizationLoadError: true }))
            .finally(() => this.setState({
                actionProjectId: null,
                actionType: null,
                authorizationLoading: false,
            }))
    }

    saveAuthorization = () => {
        const {
            authorizationProjectId,
            authorizationProject,
            authorizationUsers,
            selectedAuthorizationUserIds,
            authorizationPermissionTypes,
        } = this.state
        if (!authorizationProjectId || this.state.authorizationLoading || this.state.authorizationLoadError) return
        const grants = buildProjectAuthorizationGrants(
            selectedAuthorizationUserIds,
            authorizationUsers,
            this.isDatasetProject(authorizationProject),
            authorizationPermissionTypes,
        )
        const selectedUserIds = new Set(selectedAuthorizationUserIds.map(String))
        const databasePermissionIncreased = authorizationUsers.some(item => {
            const userId = String(item.userId)
            if (!selectedUserIds.has(userId)
                || this.isTrue(item.isOwner)
                || isSystemManagedDatasetGrant(item, this.isDatasetProject(authorizationProject))) {
                return false
            }
            const wasAuthorized = this.isTrue(item.authorized)
            const previousPermission = item.permissionType === 'readwrite' ? 'readwrite' : 'readonly'
            const nextPermission = authorizationPermissionTypes[userId] === 'readwrite' ? 'readwrite' : 'readonly'
            return !wasAuthorized || (previousPermission === 'readonly' && nextPermission === 'readwrite')
        })
        const databaseProjects = databasePermissionIncreased
            && Number(authorizationProject?.databaseReadmeLength || 0) <= 0
            ? [authorizationProject?.projectName || authorizationProject?.projectSlug || '当前应用']
            : []
        confirmAuthorizationDocumentationWarnings({ databaseProjects }, () => (
            this.executeSaveAuthorization(authorizationProjectId, grants)
        ))
    }

    executeSaveAuthorization = (authorizationProjectId, grants) => {
        this.setState({ authorizationSaving: true })
        saveVibeProjectAuthorization({
            projectId: authorizationProjectId,
            grants,
        })
            .then(() => {
                message.success('授权保存成功')
                this.setState({
                    authorizationVisible: false,
                    authorizationProjectId: null,
                    authorizationProject: null,
                    authorizationUsers: [],
                    selectedAuthorizationUserIds: [],
                    authorizationPermissionTypes: {},
                    authorizationKeyword: '',
                })
                this.loadProjects()
            })
            .catch(() => {})
            .finally(() => this.setState({ authorizationSaving: false }))
    }

    openUploadAuthorizer = project => {
        this.setState({
            uploadAuthorizerVisible: true,
            uploadAuthorizerProject: project,
        })
    }

    closeUploadAuthorizer = () => {
        this.setState({
            uploadAuthorizerVisible: false,
            uploadAuthorizerProject: null,
        })
    }

    getMissingPermissionScopes = project => {
        const scopes = []
        const databaseAvailable = Boolean(String(project?.mysqlDatabaseName || '').trim())
        if (databaseAvailable && !this.isTrue(project?.hasDatabasePermission)) {
            scopes.push('database')
        }
        if (this.isTrue(project?.apiConfigured) && !this.isTrue(project?.hasApiPermission)) {
            scopes.push('api')
        }
        return scopes
    }

    getRecommendedPermissionScope = project => {
        const scopes = this.getMissingPermissionScopes(project)
        if (scopes.includes('database') && scopes.includes('api')) return 'all'
        return scopes[0] || 'database'
    }

    getPermissionScopeText = (scope, databasePermissionType) => {
        const databaseText = databasePermissionType === 'readonly' ? '数据库只读权限' : '数据库读写权限'
        if (scope === 'api') return '接口权限'
        if (scope === 'all') return `${databaseText}和接口权限`
        return databaseText
    }

    openApplyPermission = project => {
        this.setState({
            applyVisible: true,
            applyProject: project,
            applyPermissionScope: this.getRecommendedPermissionScope(project),
            applyDatabasePermissionType: 'readonly',
        })
    }

    closeApplyPermission = () => {
        if (this.state.applying) return
        this.setState({
            applyVisible: false,
            applyProject: null,
        })
    }

    submitProjectPermission = () => {
        const {
            applyProject,
            applyPermissionScope,
            applyDatabasePermissionType,
        } = this.state
        if (!applyProject || this.state.applying) return
        this.setState({
            applying: true,
            actionProjectId: applyProject.id,
            actionType: 'apply',
        })
        applyVibeProjectPermission({
            projectId: applyProject.id,
            permissionScope: applyPermissionScope,
            databasePermissionType: applyDatabasePermissionType,
        })
            .then(() => {
                message.success('申请已提交，7天内有效')
                this.loadProjects()
                this.setState({
                    applyVisible: false,
                    applyProject: null,
                    codexGuideVisible: true,
                    codexGuideScene: VIBE_CODEX_GUIDE_SCENE.APPLICATION_SUBMITTED,
                })
            })
            .catch(() => {})
            .finally(() => this.setState({
                applying: false,
                actionProjectId: null,
                actionType: null,
            }))
    }

    getApplicableProjects = () => this.state.projects.filter(project => (
        !this.isTrue(project.isOwner)
        && this.getMissingPermissionScopes(project).length > 0
        && !this.isTrue(project.hasPendingRequest)
    ))

    // 查询条件命中的项目（不含"仅看已选"）；"全选全部"也按这个范围取
    getBatchApplyQueryProjects = () => {
        const {
            batchApplyProjects,
            batchApplyKeyword,
            batchApplyKindFilter,
            batchApplyOwnerFilter,
        } = this.state
        const byKind = filterBatchApplyProjectsByKind(batchApplyProjects, batchApplyKindFilter)
        const byOwner = this.filterProjectsByRegistrant(byKind, batchApplyOwnerFilter)
        return filterBatchApplyProjects(byOwner, batchApplyKeyword)
    }

    // 弹窗内的查询条件：关键词 + 项目用途 + 仅看已选，再交给分页展示。
    getBatchApplyFilteredProjects = () => {
        const byQuery = this.getBatchApplyQueryProjects()
        if (!this.state.batchApplyOnlySelected) return byQuery
        const selected = new Set(this.state.selectedBatchProjectIds.map(String))
        return byQuery.filter(project => selected.has(String(project.id)))
    }

    // 服务端模式下本页就是接口返回的一页；老后端（返回数组）则退回本地筛选与本地分页。
    getBatchApplyPageProjects = () => {
        if (this.state.batchApplyServerPaged) {
            return {
                currentPage: this.state.batchApplyPage,
                total: this.state.batchApplyTotal,
                pageProjects: this.state.batchApplyRows,
            }
        }
        const filtered = this.getBatchApplyFilteredProjects()
        const currentPage = getBatchApplyPage(filtered.length, this.state.batchApplyPage)
        const start = (currentPage - 1) * BATCH_APPLY_PAGE_SIZE
        return {
            currentPage,
            total: filtered.length,
            pageProjects: filtered.slice(start, start + BATCH_APPLY_PAGE_SIZE),
        }
    }

    loadBatchApplyProjects = (overrides = {}) => {
        const keyword = overrides.keyword !== undefined ? overrides.keyword : this.state.batchApplyKeyword
        const projectKind = overrides.projectKind !== undefined ? overrides.projectKind : this.state.batchApplyKindFilter
        const ownerUserIds = overrides.ownerUserIds !== undefined
            ? overrides.ownerUserIds
            : this.state.batchApplyOwnerFilter
        const page = overrides.page !== undefined ? overrides.page : this.state.batchApplyPage
        this.setState({ batchApplyTableLoading: true })
        return queryApplicableVibeProjects({
            keyword: keyword.trim(),
            projectKind: projectKind === 'all' ? '' : projectKind,
            ownerUserIds,
            applicableOnly: true,
            pageNum: page,
            pageSize: BATCH_APPLY_PAGE_SIZE,
        })
            .then(result => {
                const serverPaged = !Array.isArray(result) && Array.isArray(result?.list)
                const records = serverPaged
                    ? result.list
                    : (Array.isArray(result) ? result : [])
                // 默认不选中：拉取/翻页/筛选只刷新列表，不改变用户已有的勾选
                this.setState(prevState => ({
                    batchApplyServerPaged: serverPaged,
                    batchApplyRows: serverPaged
                        ? records
                        : records.slice((page - 1) * BATCH_APPLY_PAGE_SIZE, page * BATCH_APPLY_PAGE_SIZE),
                    batchApplyTotal: serverPaged ? Number(result.total) || 0 : records.length,
                    batchApplyRecordById: records.reduce((result2, project) => {
                        result2[String(project.id)] = project
                        return result2
                    }, {}),
                    // 老后端返回全量数组时，整份清单都可用于计算申请范围
                    batchApplyProjects: serverPaged ? prevState.batchApplyProjects : records,
                    batchApplyPage: page,
                    batchApplyTableLoading: false,
                }))
            })
            .catch(() => this.setState({ batchApplyTableLoading: false }))
    }

    handleBatchApplyKeywordChange = value => {
        // 查询范围变了，"全选全部"状态失效（已勾选的项目仍保留）
        this.setState({ batchApplyKeyword: value, batchApplyPage: 1 })
        if (this.batchApplyKeywordTimer) clearTimeout(this.batchApplyKeywordTimer)
        // 300ms 防抖，避免每敲一个字就打一次服务端筛选
        this.batchApplyKeywordTimer = setTimeout(() => {
            this.batchApplyKeywordTimer = null
            this.loadBatchApplyProjects({ keyword: value, page: 1 })
        }, 300)
    }

    handleBatchApplyFilterChange = patch => {
        // 本地模式下筛选是纯前端计算，无需请求；服务端模式需要重新拉取
        this.setState({ batchApplyPage: 1, ...patch }, () => {
            if (this.state.batchApplyServerPaged) this.loadBatchApplyProjects({ page: 1 })
        })
    }

    handleBatchApplyPageChange = page => {
        this.setState({ batchApplyPage: page })
        if (this.state.batchApplyServerPaged) this.loadBatchApplyProjects({ page })
    }

    // 跨页勾选的项目不在当前页记录里，提交前按 id 分批回查，拿到各自的"尚缺权限"范围
    fetchBatchApplyProjectsByIds = async ids => {
        if (!ids.length) return []
        const chunks = []
        for (let index = 0; index < ids.length; index += BATCH_APPLY_ID_LOOKUP_SIZE) {
            chunks.push(ids.slice(index, index + BATCH_APPLY_ID_LOOKUP_SIZE))
        }
        const collected = []
        for (const chunk of chunks) {
            try {
                const result = await queryApplicableVibeProjects({
                    projectIds: chunk,
                    pageNum: 1,
                    pageSize: BATCH_APPLY_ID_LOOKUP_SIZE,
                })
                const records = Array.isArray(result) ? result : (Array.isArray(result?.list) ? result.list : [])
                collected.push(...records)
            } catch (e) {
                // 单个分片失败不影响其他分片，查不到的项目会在提交时计入失败数
            }
        }
        if (collected.length) {
            this.setState(prevState => ({
                batchApplyRecordById: collected.reduce((accumulator, project) => {
                    accumulator[String(project.id)] = project
                    return accumulator
                }, { ...prevState.batchApplyRecordById }),
            }))
        }
        return collected
    }

    // 全选全部：按当前查询条件取回跨页的全部项目
    // 先按"不分页"请求（后端此时返回全量数组）；若后端仍然分页，再按页码逐页补齐
    fetchAllBatchApplyProjects = async () => {
        const { batchApplyKeyword, batchApplyKindFilter, batchApplyOwnerFilter } = this.state
        const params = {
            keyword: batchApplyKeyword.trim(),
            projectKind: batchApplyKindFilter === 'all' ? '' : batchApplyKindFilter,
            ownerUserIds: batchApplyOwnerFilter,
            applicableOnly: true,
        }
        const seenIds = new Set()
        // 去重：分页边界重复或后端忽略页码时，同一条只保留一次
        const collect = records => records.filter(project => {
            const id = project?.id === undefined || project?.id === null ? '' : String(project.id)
            if (!id || seenIds.has(id)) return false
            seenIds.add(id)
            return true
        })
        const first = await queryApplicableVibeProjects(params)
        if (Array.isArray(first)) return collect(first)
        const collected = collect(Array.isArray(first?.list) ? first.list : [])
        let total = Number(first?.total) || collected.length
        let pageNum = 1
        while (collected.length < total && pageNum < BATCH_APPLY_SELECT_ALL_MAX_PAGES) {
            pageNum += 1
            const next = await queryApplicableVibeProjects({
                ...params,
                pageNum,
                pageSize: BATCH_APPLY_SELECT_ALL_PAGE_SIZE,
            })
            const list = Array.isArray(next) ? next : (Array.isArray(next?.list) ? next.list : [])
            const added = collect(list)
            // 后端忽略页码、返回重复数据时直接停下，避免空转
            if (!added.length) break
            collected.push(...added)
            total = Number(next?.total) || total
        }
        return collected
    }

    // 全选：当前查询条件下的所有项目（跨页），而不是当前页
    selectAllBatchApplyProjects = () => {
        if (this.state.batchApplying || this.state.batchApplySelectAllLoading) return
        // 老后端（返回全量数组）本地就有全部记录，无需再请求
        if (!this.state.batchApplyServerPaged) {
            this.setState({
                selectedBatchProjectIds: this.getBatchApplyQueryProjects().map(project => String(project.id)),
            })
            return
        }
        this.setState({ batchApplySelectAllLoading: true, batchApplyTableLoading: true })
        this.fetchAllBatchApplyProjects()
            .then(records => {
                this.setState(prevState => ({
                    selectedBatchProjectIds: records.map(project => String(project.id)),
                    // 顺手缓存全量记录，提交时不必再按 id 回查
                    batchApplyRecordById: records.reduce((accumulator, project) => {
                        accumulator[String(project.id)] = project
                        return accumulator
                    }, { ...prevState.batchApplyRecordById }),
                    batchApplySelectAllLoading: false,
                    batchApplyTableLoading: false,
                }))
            })
            .catch(() => {
                this.setState({ batchApplySelectAllLoading: false, batchApplyTableLoading: false })
                message.error('全选失败，请重试')
            })
    }

    // 表头复选框：勾上=全选当前查询的所有项目（跨页），取消=清空全部已选
    handleBatchApplySelectAllChange = event => {
        if (event.target.checked) {
            this.selectAllBatchApplyProjects()
        } else {
            this.setState({ selectedBatchProjectIds: [] })
        }
    }

    openBatchApply = () => {
        const batchApplyProjects = this.getApplicableProjects()
        this.setState({
            batchApplyVisible: true,
            batchApplyProjects,
            batchApplyRows: batchApplyProjects.slice(0, BATCH_APPLY_PAGE_SIZE),
            batchApplyTotal: batchApplyProjects.length,
            batchApplyServerPaged: false,
            batchApplyTableLoading: false,
            batchApplyRecordById: {},
            // 默认不选中任何项目，由用户按需勾选
            selectedBatchProjectIds: [],
            batchApplySelectAllLoading: false,
            batchDatabasePermissionType: 'readonly',
            batchApplyKeyword: '',
            batchApplyKindFilter: DEFAULT_PROJECT_KIND_FILTER,
            batchApplyOwnerFilter: [],
            batchApplyOnlySelected: false,
            batchApplyPage: 1,
            batchApplyProgress: null,
        }, () => this.loadBatchApplyProjects({ page: 1 }))
    }

    closeBatchApply = () => {
        if (this.state.batchApplying) return
        if (this.batchApplyKeywordTimer) {
            clearTimeout(this.batchApplyKeywordTimer)
            this.batchApplyKeywordTimer = null
        }
        this.setState({
            batchApplyVisible: false,
            batchApplyProjects: [],
            batchApplyRows: [],
            batchApplyTotal: 0,
            batchApplyServerPaged: false,
            batchApplyTableLoading: false,
            batchApplyRecordById: {},
            selectedBatchProjectIds: [],
            batchApplySelectAllLoading: false,
            batchApplyKeyword: '',
            batchApplyKindFilter: DEFAULT_PROJECT_KIND_FILTER,
            batchApplyOwnerFilter: [],
            batchApplyOnlySelected: false,
            batchApplyPage: 1,
            batchApplyProgress: null,
        })
    }

    // 表格回传的就是完整已选集合（配合 preserveSelectedRowKeys 保留跨页勾选）
    handleBatchApplyRowSelectionChange = keys => {
        // 手动改勾选后就不再是"全选全部"状态了
        this.setState({ selectedBatchProjectIds: keys.map(key => String(key)) })
    }

    // 批量提交前二次确认，避免误点后一次性发出大量申请。
    confirmBatchApply = () => {
        const { selectedBatchProjectIds } = this.state
        if (!selectedBatchProjectIds.length || this.state.batchApplying) return
        Modal.confirm({
            title: `确认申请 ${selectedBatchProjectIds.length} 个项目的权限？`,
            content: '每个项目会申请当前尚缺的全部权限，提交后将通知项目所有者审批。',
            okText: '确认申请',
            cancelText: '再看看',
            onOk: this.applySelectedProjects,
        })
    }

    applySelectedProjects = async () => {
        const { selectedBatchProjectIds, batchApplyRecordById, batchDatabasePermissionType } = this.state
        if (!selectedBatchProjectIds.length || this.state.batchApplying) return
        this.setState({ batchApplying: true, batchApplyProgress: { done: 0, total: selectedBatchProjectIds.length } })
        // 服务端分页下勾选可能跨页，先把不在本页的记录按 id 回查出来
        const missingIds = selectedBatchProjectIds.filter(projectId => !batchApplyRecordById[String(projectId)])
        let projectById = batchApplyRecordById
        if (missingIds.length) {
            const fetched = await this.fetchBatchApplyProjectsByIds(missingIds)
            projectById = fetched.reduce((accumulator, project) => {
                accumulator[String(project.id)] = project
                return accumulator
            }, { ...batchApplyRecordById })
        }
        let successCount = 0
        let failureCount = 0
        let doneCount = 0
        // 2026-07-21 coder(lq): Submit sequentially so every project keeps the existing validation and notification flow.
        for (const projectId of selectedBatchProjectIds) {
            try {
                const project = projectById[String(projectId)]
                if (!project) {
                    failureCount += 1
                } else {
                    await applyVibeProjectPermission({
                        projectId,
                        permissionScope: this.getRecommendedPermissionScope(project),
                        databasePermissionType: batchDatabasePermissionType,
                    })
                    successCount += 1
                }
            } catch (e) {
                failureCount += 1
            }
            doneCount += 1
            this.setState({
                batchApplyProgress: { done: doneCount, total: selectedBatchProjectIds.length },
            })
        }
        if (!failureCount) {
            message.success(`已提交 ${successCount} 个项目的权限申请`)
        } else if (successCount) {
            message.warning(`已提交 ${successCount} 个申请，${failureCount} 个申请失败`)
        } else {
            message.error('所选项目申请均未提交成功')
        }
        this.setState({
            batchApplying: false,
            batchApplyVisible: false,
            batchApplyProjects: [],
            batchApplyRows: [],
            batchApplyTotal: 0,
            batchApplyServerPaged: false,
            batchApplyTableLoading: false,
            batchApplyRecordById: {},
            selectedBatchProjectIds: [],
            batchApplySelectAllLoading: false,
            batchApplyKeyword: '',
            batchApplyKindFilter: DEFAULT_PROJECT_KIND_FILTER,
            batchApplyOwnerFilter: [],
            batchApplyOnlySelected: false,
            batchApplyPage: 1,
            batchApplyProgress: null,
            codexGuideVisible: successCount > 0,
            codexGuideScene: successCount > 0
                ? VIBE_CODEX_GUIDE_SCENE.APPLICATION_SUBMITTED
                : this.state.codexGuideScene,
        })
        this.loadProjects()
    }

    renderBatchApplyModal = () => {
        const {
            batchApplyVisible,
            batchApplyProjects,
            selectedBatchProjectIds,
            batchDatabasePermissionType,
            batchApplyKeyword,
            batchApplyKindFilter,
            batchApplyOnlySelected,
            batchApplying,
            batchApplyProgress,
            batchApplyServerPaged,
            batchApplyTableLoading,
            batchApplyRecordById,
            batchApplyTotal,
            batchApplySelectAllLoading,
        } = this.state
        const { currentPage, pageProjects } = this.getBatchApplyPageProjects()
        // "全选全部"的范围是当前查询条件下的所有项目（跨页），不是当前页
        const selectAllTotal = batchApplyServerPaged ? batchApplyTotal : this.getBatchApplyQueryProjects().length
        const selectedInView = selectedBatchProjectIds.filter(id => batchApplyRecordById[String(id)]).length
        // 服务端分页下后端只回当前页，缓存里没有的已选项只能提示"不在本页"
        const selectedOutsideView = selectedBatchProjectIds.length - selectedInView
        const ownerOptions = this.getRegistrantOptions(batchApplyProjects)
        const hasQuery = Boolean(batchApplyKeyword.trim())
            || batchApplyKindFilter !== DEFAULT_PROJECT_KIND_FILTER
            || this.state.batchApplyOwnerFilter.length > 0
            || batchApplyOnlySelected
        return (
            <Modal
                title="一键申请项目权限"
                visible={batchApplyVisible}
                width={860}
                okText={`确认申请（${selectedBatchProjectIds.length}）`}
                cancelText="取消"
                confirmLoading={batchApplying}
                okButtonProps={{ disabled: !selectedBatchProjectIds.length }}
                onOk={this.confirmBatchApply}
                onCancel={this.closeBatchApply}
                closable={!batchApplying}
                maskClosable={!batchApplying}
                destroyOnClose
            >
                <div style={{ marginBottom: 12, color: 'rgba(0, 0, 0, 0.45)' }}>
                    默认不选中项目，请勾选需要申请的项目；点列表表头的复选框可全选当前查询的所有项目（含其他页）。
                </div>
                <Space wrap style={{ marginBottom: 12 }} size={8}>
                    <Input.Search
                        allowClear
                        placeholder="搜索项目名称 / 标识 / 所有者 / 简介"
                        value={batchApplyKeyword}
                        style={{ width: 280 }}
                        onChange={event => this.handleBatchApplyKeywordChange(event.target.value)}
                    />
                    <Select
                        value={batchApplyKindFilter}
                        style={{ width: 116 }}
                        onChange={value => this.handleBatchApplyFilterChange({ batchApplyKindFilter: value })}
                    >
                        <Select.Option value="application">Vibe项目</Select.Option>
                        <Select.Option value="dataset">AIC项目</Select.Option>
                        <Select.Option value="all">全部用途</Select.Option>
                    </Select>
                    <Select
                        mode="multiple"
                        showSearch
                        allowClear
                        maxTagCount="responsive"
                        optionFilterProp="children"
                        placeholder="全部所有者"
                        value={this.state.batchApplyOwnerFilter}
                        style={{ width: 176 }}
                        onChange={value => this.handleBatchApplyFilterChange({ batchApplyOwnerFilter: value })}
                    >
                        {ownerOptions.map(option => (
                            <Select.Option key={option.value} value={option.value}>
                                {option.label}
                            </Select.Option>
                        ))}
                    </Select>
                    {batchApplyServerPaged ? null : (
                        <Button
                            type={batchApplyOnlySelected ? 'primary' : 'default'}
                            onClick={() => this.setState(prevState => ({
                                batchApplyOnlySelected: !prevState.batchApplyOnlySelected,
                                batchApplyPage: 1,
                            }))}
                        >
                            仅看已选（{selectedInView}）
                        </Button>
                    )}
                    {hasQuery ? (
                        <Button
                            type="link"
                            onClick={() => {
                                if (this.batchApplyKeywordTimer) {
                                    clearTimeout(this.batchApplyKeywordTimer)
                                    this.batchApplyKeywordTimer = null
                                }
                                this.setState({
                                    batchApplyKeyword: '',
                                    batchApplyKindFilter: DEFAULT_PROJECT_KIND_FILTER,
                                    batchApplyOwnerFilter: [],
                                    batchApplyOnlySelected: false,
                                    batchApplyPage: 1,
                                }, () => this.loadBatchApplyProjects({
                                    keyword: '',
                                    projectKind: DEFAULT_PROJECT_KIND_FILTER,
                                    ownerUserIds: [],
                                    page: 1,
                                }))
                            }}
                        >
                            重置查询
                        </Button>
                    ) : null}
                </Space>
                {batchApplying && batchApplyProgress ? (
                    <div style={{ marginBottom: 8 }}>
                        <div style={{ marginBottom: 4 }}>
                            <Text type="secondary">
                                正在提交：{batchApplyProgress.done}/{batchApplyProgress.total}，请勿关闭弹窗
                            </Text>
                        </div>
                        <Progress
                            size="small"
                            percent={Math.round((batchApplyProgress.done / batchApplyProgress.total) * 100)}
                        />
                    </div>
                ) : null}
                <Table
                    rowKey={record => String(record.id)}
                    size="small"
                    loading={batchApplyTableLoading}
                    pagination={{
                        pageSize: BATCH_APPLY_PAGE_SIZE,
                        showSizeChanger: false,
                        current: currentPage,
                        total: batchApplyTotal,
                        showTotal: (total, range) => {
                            const selectedText = selectedBatchProjectIds.length
                                ? ` · 已选 ${selectedBatchProjectIds.length} 个`
                                    + (selectedOutsideView > 0 ? `（其中 ${selectedOutsideView} 个不在当前查询结果内）` : '')
                                : ''
                            return `第 ${range[0]}-${range[1]} 条 / 共 ${total} 条${selectedText}`
                        },
                        onChange: this.handleBatchApplyPageChange,
                    }}
                    dataSource={pageProjects}
                    locale={{ emptyText: hasQuery ? '没有符合查询条件的项目' : '暂无可以申请的项目' }}
                    rowSelection={{
                        selectedRowKeys: selectedBatchProjectIds,
                        // 服务端分页下勾选要跨页保留，否则翻页时 antd 会丢掉不在本页的 key
                        preserveSelectedRowKeys: true,
                        // 隐藏 antd 自带的"本页全选"，表头换成"全选当前查询的所有项目（跨页）"
                        hideSelectAll: true,
                        columnTitle: (
                            <Tooltip title={`全选当前查询的所有项目（共 ${selectAllTotal} 个，含其他页）`}>
                                <Checkbox
                                    checked={selectAllTotal > 0 && selectedBatchProjectIds.length >= selectAllTotal}
                                    indeterminate={selectedBatchProjectIds.length > 0
                                        && selectedBatchProjectIds.length < selectAllTotal}
                                    disabled={!selectAllTotal || batchApplying || batchApplySelectAllLoading}
                                    onChange={this.handleBatchApplySelectAllChange}
                                />
                            </Tooltip>
                        ),
                        onChange: this.handleBatchApplyRowSelectionChange,
                    }}
                    columns={[
                        {
                            title: '项目',
                            key: 'project',
                            width: 220,
                            render: (_, record) => (
                                <div>
                                    <div>{record.projectName || '-'}</div>
                                    <Text type="secondary" code style={{ fontSize: 12 }}>{record.projectSlug || '-'}</Text>
                                </div>
                            ),
                        },
                        {
                            title: '申请范围',
                            key: 'scope',
                            width: 180,
                            render: (_, record) => {
                                const scope = this.getRecommendedPermissionScope(record)
                                return this.getPermissionScopeText(scope, batchDatabasePermissionType)
                            },
                        },
                        {
                            title: '所有者',
                            key: 'owner',
                            width: 140,
                            render: (_, record) => record.ownerUserNick || record.ownerUserName || record.ownerUserId || '-',
                        },
                        {
                            title: '项目简介',
                            dataIndex: 'description',
                            ellipsis: true,
                            render: value => value || '-',
                        },
                    ]}
                />
            </Modal>
        )
    }

    canDeleteProject = project => canDeleteVibeProject(project, this.props.userInfo)

    openProjectDelete = record => {
        this.setState({
            deleteVisible: true,
            deleteProject: record,
        })
    }

    closeProjectDelete = () => {
        this.setState({
            deleteVisible: false,
            deleteProject: null,
        })
    }

    isDatasetProject = project => getVibeProjectKind(project) === 'dataset'

    renderAction = (record, showDelete = false) => {
        const { actionProjectId, actionType } = this.state
        const isLoading = actionProjectId === record.id
        let primaryAction
        if (this.isTrue(record.isOwner)) {
            primaryAction = (
                <Button
                    type="link"
                    size="small"
                    loading={isLoading && actionType === 'authorize'}
                    onClick={() => this.openAuthorization(record)}
                >
                    授权
                </Button>
            )
        } else if (this.isTrue(record.hasPendingRequest)) {
            primaryAction = <Text type="warning">申请中</Text>
        } else if (this.getMissingPermissionScopes(record).length > 0) {
            primaryAction = (
                <Button
                    type="link"
                    size="small"
                    loading={isLoading && actionType === 'apply'}
                    onClick={() => this.openApplyPermission(record)}
                >
                    {this.isTrue(record.hasPermission) ? '申请其他权限' : '申请'}
                </Button>
            )
        } else {
            primaryAction = this.isTrue(record.hasPermission)
                ? <Text type="secondary">已有全部权限</Text>
                : <Text type="secondary">暂无可申请权限</Text>
        }
        const deleteAction = showDelete && this.canDeleteProject(record) ? (
            <Button
                type="link"
                danger
                size="small"
                icon={<DeleteOutlined />}
                onClick={() => this.openProjectDelete(record)}
            >
                {record.status === 'deleting' ? '查看删除' : '删除'}
            </Button>
        ) : null
        const uploadAuthorizerAction = this.isTrue(record.isOwner) && this.isDatasetProject(record) ? (
            <Button
                type="link"
                size="small"
                icon={<TeamOutlined />}
                onClick={() => this.openUploadAuthorizer(record)}
            >
                授权上传数据
            </Button>
        ) : null
        return uploadAuthorizerAction || deleteAction
            ? <Space size={2}>{primaryAction}{uploadAuthorizerAction}{deleteAction}</Space>
            : primaryAction
    }

    renderApplyPermissionModal = () => {
        const {
            applyVisible,
            applyProject,
            applyPermissionScope,
            applyDatabasePermissionType,
            applying,
        } = this.state
        const missingScopes = this.getMissingPermissionScopes(applyProject)
        const databaseAvailable = missingScopes.includes('database')
        const apiAvailable = missingScopes.includes('api')
        return (
            <Modal
                title={`申请项目权限：${applyProject?.projectName || '-'}`}
                visible={applyVisible}
                okText="确认申请"
                cancelText="取消"
                confirmLoading={applying}
                onOk={this.submitProjectPermission}
                onCancel={this.closeApplyPermission}
                closable={!applying}
                maskClosable={!applying}
                destroyOnClose
            >
                <div style={{ marginBottom: 18 }}>
                    <div style={{ marginBottom: 8 }}>授权范围</div>
                    <Radio.Group
                        value={applyPermissionScope}
                        onChange={event => this.setState({ applyPermissionScope: event.target.value })}
                    >
                        <Space direction="vertical">
                            <Radio value="database" disabled={!databaseAvailable}>数据库权限</Radio>
                            <Radio value="api" disabled={!apiAvailable}>接口权限</Radio>
                            <Radio value="all" disabled={!databaseAvailable || !apiAvailable}>数据库和接口权限</Radio>
                        </Space>
                    </Radio.Group>
                </div>
                {applyPermissionScope !== 'api' ? (
                    <div>
                        <div style={{ marginBottom: 8 }}>数据库权限类型</div>
                        <Radio.Group
                            value={applyDatabasePermissionType}
                            onChange={event => this.setState({ applyDatabasePermissionType: event.target.value })}
                        >
                            <Radio value="readonly">只读</Radio>
                            <Radio value="readwrite">读写</Radio>
                        </Radio.Group>
                    </div>
                ) : null}
            </Modal>
        )
    }

    renderAiRecommendation = () => {
        const { aiRequirement, aiLoading, aiResult } = this.state
        const recommendations = aiResult?.recommendations || []
        return (
            <Card
                className="profile-vibe-ai-card"
                title={<span><BulbOutlined /> 问 AI 找数据</span>}
                extra={<Text type="secondary">AI 会根据项目的数据说明推荐，最多返回 5 个项目</Text>}
            >
                <Input.Search
                    allowClear
                    size="large"
                    maxLength={500}
                    value={aiRequirement}
                    loading={aiLoading}
                    enterButton="AI 推荐项目"
                    placeholder="例如：我想分析各渠道销售预测、实际订单和目标达成情况"
                    onChange={event => this.setState({ aiRequirement: event.target.value })}
                    onSearch={this.recommendProjects}
                />
                {aiResult ? (
                    <div className="profile-vibe-ai-result-wrap">
                        <Alert
                            showIcon
                            type={recommendations.length > 0 ? 'info' : 'warning'}
                            message={aiResult.answer || (recommendations.length > 0 ? '已为你找到相关项目' : '没有找到匹配项目')}
                        />
                        {recommendations.length > 0 ? (
                            <div className="profile-vibe-ai-result-list">
                                {recommendations.map(record => (
                                    <div className="profile-vibe-ai-result-item" key={record.id}>
                                        <div className="profile-vibe-ai-result-head">
                                            <div>
                                                <Text strong><DatabaseOutlined /> {record.projectName || '-'}</Text>
                                                <Text type="secondary" code className="profile-vibe-ai-project-slug">
                                                    {record.projectSlug || '-'}
                                                </Text>
                                            </div>
                                            <Space>
                                                {this.renderProjectKind(record.projectKind)}
                                                {this.isTrue(record.isOwner) ? <Tag color="blue">我创建的</Tag> : null}
                                                {!this.isTrue(record.isOwner) && this.isTrue(record.hasPermission)
                                                    ? <Tag color="green">已有权限</Tag>
                                                    : null}
                                                {!this.isTrue(record.isOwner) && !this.isTrue(record.hasPermission)
                                                    ? <Tag color="orange">需申请权限</Tag>
                                                    : null}
                                                {this.renderAction(record)}
                                            </Space>
                                        </div>
                                        <div className="profile-vibe-ai-reason">
                                            <Text strong>推荐理由：</Text>{record.reason || '项目数据与需求相关'}
                                        </div>
                                        <div className="profile-vibe-ai-summary">
                                            <Text type="secondary">数据说明：{record.dataSummary || '-'}</Text>
                                        </div>
                                        {Array.isArray(record.matchedData) && record.matchedData.length > 0 ? (
                                            <div className="profile-vibe-ai-matches">
                                                <Text type="secondary">匹配数据：</Text>
                                                {record.matchedData.map(item => <Tag key={item}>{item}</Tag>)}
                                            </div>
                                        ) : null}
                                        <div className="profile-vibe-ai-owner">
                                            <Text type="secondary">
                                                项目所有者：{record.ownerUserNick || record.ownerUserName || record.ownerUserId || '-'}
                                            </Text>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : null}
                    </div>
                ) : null}
            </Card>
        )
    }

    renderAuthorizationModal = () => {
        const {
            authorizationVisible,
            authorizationLoading,
            authorizationLoadError,
            authorizationSaving,
            authorizationProject,
            authorizationUsers,
            selectedAuthorizationUserIds,
            authorizationPermissionTypes,
            authorizationKeyword,
        } = this.state
        const normalizedKeyword = authorizationKeyword.trim().toLowerCase()
        const filteredAuthorizationUsers = normalizedKeyword
            ? authorizationUsers.filter(item => (
                [item.userNick, item.userName, item.userId, item.mysqlUsername]
                    .some(value => String(value || '').toLowerCase().includes(normalizedKeyword))
            ))
            : authorizationUsers
        const datasetProject = this.isDatasetProject(authorizationProject)
        const policy = getDatasetPolicyMeta(authorizationProject?.sensitivityLevel)
        return (
            <Modal
                title={datasetProject ? 'AIC项目授权' : 'Vibe项目授权'}
                visible={authorizationVisible}
                width={980}
                confirmLoading={authorizationSaving}
                okButtonProps={{ disabled: authorizationLoading || authorizationLoadError }}
                okText="保存授权"
                cancelText="取消"
                onOk={this.saveAuthorization}
                onCancel={() => this.setState({
                    authorizationVisible: false,
                    authorizationProjectId: null,
                    authorizationProject: null,
                    authorizationUsers: [],
                    selectedAuthorizationUserIds: [],
                    authorizationPermissionTypes: {},
                    authorizationKeyword: '',
                    authorizationLoadError: false,
                })}
                destroyOnClose
            >
                <Spin spinning={authorizationLoading}>
                    {authorizationLoadError ? (
                        <Alert type="error" showIcon message="授权人员加载失败，请关闭弹窗后重试。" style={{ marginBottom: 12 }} />
                    ) : datasetProject ? (
                        <Alert
                            showIcon
                            type={authorizationProject?.sensitivityLevel === 'L3' ? 'error' : 'info'}
                            message={policy.text}
                            description={`${policy.summary} 系统默认授权由敏感级和一级部门自动维护，不能在这里取消；手工授权可选择只读或读写权限。`}
                            style={{ marginBottom: 12 }}
                        />
                    ) : (
                        <div style={{ marginBottom: 12, color: 'rgba(0, 0, 0, 0.45)' }}>
                            可选择全部有效用户并分别设置只读或读写权限。未开通 Vibe 空间的用户会在保存时使用个人开通流程自动开通 GitLab 和数据库账号。
                        </div>
                    )}
                    <Input.Search
                        allowClear
                        value={authorizationKeyword}
                        placeholder="搜索姓名、登录账号或 Vibe 账号"
                        onChange={event => this.setState({ authorizationKeyword: event.target.value })}
                        style={{ width: 320, marginBottom: 12 }}
                    />
                    <Table
                        key={authorizationKeyword}
                        rowKey={record => String(record.userId)}
                        size="small"
                        pagination={{ pageSize: 10, showSizeChanger: false }}
                        dataSource={filteredAuthorizationUsers}
                        locale={{ emptyText: '暂无可授权人员' }}
                        rowSelection={{
                            preserveSelectedRowKeys: true,
                            selectedRowKeys: selectedAuthorizationUserIds,
                            onChange: keys => this.setState({
                                selectedAuthorizationUserIds: keys.map(key => String(key)),
                            }),
                            getCheckboxProps: record => ({
                                disabled: this.isTrue(record.isOwner)
                                    || (datasetProject && isSystemManagedDatasetGrant(record, true))
                                    || (!this.isTrue(record.spaceEnabled) && !this.isTrue(record.canProvision)),
                            }),
                        }}
                        columns={[
                            {
                                title: '人员',
                                key: 'user',
                                render: (_, record) => (
                                    <div>
                                        <div>
                                            {record.userNick || record.userName || record.userId || '-'}
                                            {this.isTrue(record.isOwner) ? <Tag color="blue" style={{ marginLeft: 8 }}>项目所有者</Tag> : null}
                                            {datasetProject && isSystemManagedDatasetGrant(record, true)
                                                ? <Tag color="cyan" style={{ marginLeft: 8 }}>系统默认</Tag>
                                                : null}
                                        </div>
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            {record.userName || record.userId || '-'}
                                        </Text>
                                    </div>
                                ),
                            },
                            {
                                title: 'Vibe空间',
                                key: 'spaceStatus',
                                width: 120,
                                render: (_, record) => {
                                    if (this.isTrue(record.spaceEnabled)) return <Tag color="green">已开通</Tag>
                                    if (record.accountStatus === 'disabled') return <Tag color="red">已停用</Tag>
                                    if (this.isTrue(record.canProvision)) return <Tag color="gold">未开通</Tag>
                                    if (record.accountStatus) return <Tag color="red">账号异常</Tag>
                                    return <Tag>缺少公司邮箱</Tag>
                                },
                            },
                            {
                                title: 'Vibe账号',
                                dataIndex: 'mysqlUsername',
                                width: 160,
                                render: (value, record) => (
                                    value
                                        ? <Text code>{value}</Text>
                                        : <Text type="secondary">{this.isTrue(record.canProvision) ? '授权时创建' : '-'}</Text>
                                ),
                            },
                            {
                                title: '当前权限',
                                dataIndex: 'permissionType',
                                width: 100,
                                render: (value, record) => (
                                    this.isTrue(record.authorized)
                                        ? <Tag color="green">{value === 'readonly' ? '只读' : '读写'}</Tag>
                                        : <Text type="secondary">未授权</Text>
                                ),
                            },
                            {
                                title: '授权权限',
                                key: 'grantPermissionType',
                                width: 120,
                                render: (_, record) => {
                                    const userId = String(record.userId)
                                    const systemManaged = datasetProject
                                        && isSystemManagedDatasetGrant(record, true)
                                    if (this.isTrue(record.isOwner) || systemManaged) {
                                        const permissionType = this.isTrue(record.isOwner)
                                            ? 'readwrite'
                                            : (record.permissionType || 'readonly')
                                        return (
                                            <Tag color={permissionType === 'readonly' ? 'blue' : 'green'}>
                                                {permissionType === 'readonly' ? '只读' : '读写'}
                                            </Tag>
                                        )
                                    }
                                    return (
                                        <Select
                                            size="small"
                                            value={authorizationPermissionTypes[userId] || 'readonly'}
                                            disabled={!selectedAuthorizationUserIds.includes(userId)}
                                            style={{ width: 88 }}
                                            onChange={permissionType => this.setState(prevState => ({
                                                authorizationPermissionTypes: {
                                                    ...prevState.authorizationPermissionTypes,
                                                    [userId]: permissionType,
                                                },
                                            }))}
                                        >
                                            <Select.Option value="readonly">只读</Select.Option>
                                            <Select.Option value="readwrite">读写</Select.Option>
                                        </Select>
                                    )
                                },
                            },
                            ...(datasetProject ? [{
                                title: '授权来源',
                                key: 'grantSource',
                                width: 110,
                                render: (_, record) => (
                                    isSystemManagedDatasetGrant(record, true)
                                        ? <Tag color="cyan">系统策略</Tag>
                                        : (this.isTrue(record.authorized)
                                            ? <Tag color="blue">手工授权</Tag>
                                            : <Text type="secondary">-</Text>)
                                ),
                            }] : []),
                        ]}
                    />
                </Spin>
            </Modal>
        )
    }

    renderUploadAuthorizerModal = () => {
        return (
            <VibeUploadAuthorizerDialog
                visible={this.state.uploadAuthorizerVisible}
                project={this.state.uploadAuthorizerProject}
                onClose={this.closeUploadAuthorizer}
                onSaved={this.loadProjects}
            />
        )
    }

    loadPermissionRequests = () => {
        this.setState({ requestLoading: true, requestLoadError: false })
        getMyVibeProjectPermissionRequestList({})
            .then(requests => {
                const permissionRequests = Array.isArray(requests) ? [...requests] : []
                const { focusedRequestId } = this.state
                if (focusedRequestId) {
                    permissionRequests.sort((left, right) => {
                        const leftFocused = String(left.id) === String(focusedRequestId)
                        const rightFocused = String(right.id) === String(focusedRequestId)
                        return leftFocused === rightFocused ? 0 : (leftFocused ? -1 : 1)
                    })
                }
                this.setState({
                    permissionRequests,
                    selectedRequestIds: permissionRequests
                        .filter(request => request.status === 'pending')
                        .map(request => String(request.id)),
                })
            })
            .catch(() => this.setState({ requestLoadError: true }))
            .finally(() => this.setState({ requestLoading: false }))
    }

    openPermissionRequests = () => {
        this.setState({ requestVisible: true, focusedRequestId: null })
        this.loadPermissionRequests()
    }

    closePermissionRequests = () => {
        const { history, location } = this.props
        const search = new URLSearchParams(location?.search)
        search.delete('requestId')
        history.replace({
            pathname: location.pathname,
            search: search.toString(),
        })
        this.setState({
            requestVisible: false,
            focusedRequestId: null,
            selectedRequestIds: [],
        })
    }

    approvePermissionRequest = record => {
        const applicantName = record.applicantUserNick || record.applicantUserName || record.applicantUserId || '申请人'
        const permissionText = this.getPermissionScopeText(
            record.permissionScope || 'database',
            record.databasePermissionType || 'readwrite',
        )
        Modal.confirm({
            title: `同意 ${applicantName} 的权限申请？`,
            content: `同意后将授予“${record.projectName || '-'}”项目的${permissionText}。`,
            okText: '同意并授权',
            cancelText: '取消',
            onOk: () => {
                this.setState({ approvingRequestId: record.id })
                return approveVibeProjectPermissionRequest({ requestId: record.id })
                    .then(() => {
                        message.success('已同意并完成授权')
                        this.loadPermissionRequests()
                        this.loadPendingRequestCount()
                        this.loadProjects()
                    })
                    .finally(() => this.setState({ approvingRequestId: null }))
            },
        })
    }

    approveSelectedPermissionRequests = async () => {
        const { selectedRequestIds } = this.state
        if (!selectedRequestIds.length || this.state.batchApproving) return
        this.setState({ batchApproving: true })
        let successCount = 0
        let failureCount = 0
        // 2026-07-21 coder(lq): Process grants sequentially so concurrent updates cannot drop another project permission.
        for (const requestId of selectedRequestIds) {
            try {
                await approveVibeProjectPermissionRequest({ requestId })
                successCount += 1
            } catch (e) {
                failureCount += 1
            }
        }
        if (!failureCount) {
            message.success(`已完成 ${successCount} 个权限授权`)
        } else if (successCount) {
            message.warning(`已完成 ${successCount} 个授权，${failureCount} 个授权失败`)
        } else {
            message.error('所选权限申请均未授权成功')
        }
        this.setState({ batchApproving: false })
        this.loadPermissionRequests()
        this.loadPendingRequestCount()
        this.loadProjects()
    }

    renderPermissionRequestStatus = status => {
        const statusMeta = {
            pending: { text: '待授权', color: 'orange' },
            approved: { text: '已同意', color: 'green' },
            expired: { text: '已过期', color: 'default' },
        }
        const meta = statusMeta[(status || '').toLowerCase()] || { text: status || '-', color: 'default' }
        return <Tag color={meta.color}>{meta.text}</Tag>
    }

    renderPermissionRequestModal = () => {
        const {
            requestVisible,
            requestLoading,
            requestLoadError,
            permissionRequests,
            approvingRequestId,
            selectedRequestIds,
            batchApproving,
            focusedRequestId,
        } = this.state
        return (
            <Modal
                title="谁申请了我的项目"
                visible={requestVisible}
                width={1000}
                footer={[
                    <Button key="close" onClick={this.closePermissionRequests} disabled={batchApproving}>关闭</Button>,
                    <Button
                        key="batchApprove"
                        type="primary"
                        icon={<CheckCircleOutlined />}
                        loading={batchApproving}
                        disabled={!selectedRequestIds.length}
                        onClick={this.approveSelectedPermissionRequests}
                    >
                        一键授权（{selectedRequestIds.length}）
                    </Button>,
                ]}
                onCancel={this.closePermissionRequests}
                closable={!batchApproving}
                maskClosable={!batchApproving}
                destroyOnClose
            >
                {requestLoadError ? (
                    <Alert
                        type="error"
                        showIcon
                        message="权限申请加载失败"
                        action={<Button size="small" onClick={this.loadPermissionRequests}>重试</Button>}
                        style={{ marginBottom: 12 }}
                    />
                ) : null}
                <Spin spinning={requestLoading}>
                    <Table
                        rowKey={record => String(record.id)}
                        size="small"
                        dataSource={permissionRequests}
                        pagination={{ pageSize: 10, showSizeChanger: false }}
                        locale={{ emptyText: '暂时没有人申请你的项目' }}
                        scroll={{ x: 900 }}
                        rowSelection={{
                            selectedRowKeys: selectedRequestIds,
                            onChange: keys => this.setState({
                                selectedRequestIds: keys.map(key => String(key)),
                            }),
                            getCheckboxProps: record => ({ disabled: record.status !== 'pending' }),
                        }}
                        rowClassName={record => (
                            focusedRequestId && String(record.id) === String(focusedRequestId)
                                ? 'profile-vibe-permission-request-focused'
                                : ''
                        )}
                        columns={[
                            {
                                title: '申请人',
                                key: 'applicant',
                                width: 180,
                                render: (_, record) => (
                                    <div>
                                        <div>{record.applicantUserNick || record.applicantUserName || record.applicantUserId || '-'}</div>
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            {record.applicantUserName || record.applicantUserId || '-'}
                                        </Text>
                                    </div>
                                ),
                            },
                            {
                                title: '申请项目',
                                key: 'project',
                                width: 200,
                                render: (_, record) => (
                                    <div>
                                        <div>{record.projectName || '-'}</div>
                                        <Text type="secondary" code style={{ fontSize: 12 }}>{record.projectSlug || '-'}</Text>
                                    </div>
                                ),
                            },
                            {
                                title: '申请范围',
                                key: 'permissionScope',
                                width: 170,
                                render: (_, record) => this.getPermissionScopeText(
                                    record.permissionScope || 'database',
                                    record.databasePermissionType || 'readwrite',
                                ),
                            },
                            {
                                title: '申请时间',
                                dataIndex: 'createTime',
                                width: 150,
                                render: this.formatDate,
                            },
                            {
                                title: '有效期至',
                                dataIndex: 'expireTime',
                                width: 150,
                                render: this.formatDate,
                            },
                            {
                                title: '状态',
                                dataIndex: 'status',
                                width: 90,
                                render: this.renderPermissionRequestStatus,
                            },
                            {
                                title: '操作',
                                key: 'action',
                                fixed: 'right',
                                width: 100,
                                render: (_, record) => record.status === 'pending' ? (
                                    <Button
                                        type="link"
                                        size="small"
                                        loading={String(approvingRequestId) === String(record.id)}
                                        disabled={batchApproving}
                                        onClick={() => this.approvePermissionRequest(record)}
                                    >
                                        同意
                                    </Button>
                                ) : <Text type="secondary">-</Text>,
                            },
                        ]}
                    />
                </Spin>
            </Modal>
        )
    }

    getColumns = () => ([
        {
            title: '项目名称',
            dataIndex: 'projectName',
            key: 'projectName',
            width: 220,
            render: (value, record) => (
                <Space direction="vertical" size={0}>
                    <Text strong>{this.isDatasetProject(record) ? <DatabaseOutlined /> : <CloudServerOutlined />} {value || '-'}</Text>
                    <Text type="secondary" code>{record.projectSlug || '-'}</Text>
                </Space>
            ),
        },
        {
            title: '登记人',
            key: 'owner',
            width: 140,
            render: (_, record) => record.ownerUserNick || record.ownerUserName || record.ownerUserId || '-',
        },
        {
            title: '数据 Owner',
            dataIndex: 'dataOwner',
            key: 'dataOwner',
            width: 180,
            ellipsis: true,
            render: (value, record) => this.isDatasetProject(record) ? (value || '-') : <Text type="secondary">-</Text>,
        },
        {
            title: '当前位置',
            dataIndex: 'dataLocation',
            key: 'dataLocation',
            width: 260,
            ellipsis: true,
            render: (value, record) => this.isDatasetProject(record) ? (value || '-') : <Text type="secondary">-</Text>,
        },
        {
            title: '项目简介',
            dataIndex: 'description',
            key: 'description',
            ellipsis: true,
            render: value => value || '-',
        },
        {
            title: '数据说明',
            dataIndex: 'dataSummary',
            key: 'dataSummary',
            width: 360,
            ellipsis: true,
            render: value => value || <Text type="secondary">暂无数据说明</Text>,
        },
        {
            title: '项目用途',
            dataIndex: 'projectKind',
            key: 'projectKind',
            width: 110,
            render: this.renderProjectKind,
        },
        {
            title: '运行方式',
            dataIndex: 'deploymentMode',
            key: 'deploymentMode',
            width: 120,
            render: this.renderDeploymentMode,
        },
        {
            title: '数据权限',
            key: 'datasetPolicy',
            width: 180,
            render: (_, record) => {
                if (!this.isDatasetProject(record)) return <Text type="secondary">-</Text>
                const policy = getDatasetPolicyMeta(record.sensitivityLevel)
                return (
                    <Space direction="vertical" size={0}>
                        <Tag color={policy.color}>{record.sensitivityLevel || 'L1'}</Tag>
                        <Text type="secondary">{record.ownerDepartmentName || '未设置一级部门'}</Text>
                    </Space>
                )
            },
        },
        {
            title: '更新频率',
            key: 'updateFrequency',
            width: 130,
            render: (_, record) => this.isDatasetProject(record)
                ? this.getUpdateFrequencyText(record)
                : <Text type="secondary">-</Text>,
        },
        {
            title: 'AIC 数据状态',
            key: 'aicDataStatus',
            width: 170,
            render: this.renderAicDataStatus,
        },
        {
            title: '主体',
            dataIndex: 'businessSubject',
            key: 'businessSubject',
            width: 90,
            render: this.renderBusinessSubject,
        },
        {
            title: '状态',
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: this.renderStatus,
        },
        {
            title: '创建时间',
            dataIndex: 'createTime',
            key: 'createTime',
            width: 160,
            render: this.formatDate,
        },
        {
            title: '操作',
            key: 'action',
            fixed: 'right',
            width: this.state.projects.some(this.canDeleteProject) ? 320 : 230,
            render: (_, record) => this.renderAction(record, true),
        },
    ])

    render() {
        const {
            loading,
            loadError,
            projects,
            projectFilter,
            projectKindFilter,
            registrantFilter,
            pendingRequestCount,
            requestCountLoading,
        } = this.state
        const registrantOptions = this.getRegistrantOptions(projects)
        const registrantFilteredProjects = this.filterProjectsByRegistrant(projects, registrantFilter)
        const kindFilteredProjects = this.filterProjects(registrantFilteredProjects, 'all', projectKindFilter)
        const ownedProjectCount = this.filterProjects(registrantFilteredProjects, 'owned', projectKindFilter).length
        const permittedProjectCount = this.filterProjects(registrantFilteredProjects, 'permitted', projectKindFilter).length
        const filteredProjects = this.filterProjects(registrantFilteredProjects, projectFilter, projectKindFilter)
        if (loadError) {
            return (
                <Result
                    status="error"
                    title="Vibe 项目列表加载失败"
                    subTitle="无法获取全部 Vibe 项目，请稍后重试"
                    extra={<Button type="primary" onClick={this.loadProjects}>重试</Button>}
                />
            )
        }

        return (
            <div className="profile-vibe-wrap profile-vibe-project-list-wrap">
                {this.renderAiRecommendation()}
                <div className="profile-vibe-toolbar">
                    <div className="profile-market-toolbar-title">Vibe 项目列表</div>
                    <Space wrap>
                        <Space size={6}>
                            <Text type="secondary">登记人</Text>
                            <Select
                                mode="multiple"
                                showSearch
                                allowClear
                                maxTagCount="responsive"
                                optionFilterProp="children"
                                placeholder="全部登记人"
                                value={registrantFilter}
                                style={{ width: 240 }}
                                onChange={value => this.updateListFilter({ registrantFilter: value })}
                            >
                                {registrantOptions.map(option => (
                                    <Select.Option key={option.value} value={option.value}>
                                        {option.label}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Space>
                        <Space size={6}>
                            <Text type="secondary">项目用途</Text>
                            <Select
                                value={projectKindFilter}
                                style={{ width: 132 }}
                                onChange={value => this.updateListFilter({ projectKindFilter: value })}
                            >
                                <Select.Option value="dataset">AIC项目</Select.Option>
                                <Select.Option value="application">Vibe项目</Select.Option>
                                <Select.Option value="all">全部</Select.Option>
                            </Select>
                        </Space>
                        <Radio.Group
                            value={projectFilter}
                            buttonStyle="solid"
                            onChange={event => this.updateListFilter({ projectFilter: event.target.value })}
                        >
                            <Radio.Button value="all">所有项目（{kindFilteredProjects.length}）</Radio.Button>
                            <Radio.Button value="owned">我创建的项目（{ownedProjectCount}）</Radio.Button>
                            <Radio.Button value="permitted">我有权限的别人项目（{permittedProjectCount}）</Radio.Button>
                        </Radio.Group>
                        <Button
                            type="primary"
                            danger
                            icon={<DatabaseOutlined />}
                            onClick={() => this.setState({
                                codexGuideVisible: true,
                                codexGuideScene: VIBE_CODEX_GUIDE_SCENE.NORMAL,
                            })}
                        >
                            申请和授权有啥用？
                        </Button>
                        <Button
                            type="primary"
                            icon={<SendOutlined />}
                            onClick={this.openBatchApply}
                        >
                            一键申请
                        </Button>
                        <Badge count={pendingRequestCount} overflowCount={99} offset={[-2, 2]}>
                            <Button
                                icon={<TeamOutlined />}
                                loading={requestCountLoading}
                                onClick={this.openPermissionRequests}
                            >
                                谁申请了我的项目
                            </Button>
                        </Badge>
                        <Button icon={<ReloadOutlined />} onClick={this.refreshPage}>刷新</Button>
                    </Space>
                </div>
                <Spin spinning={loading}>
                    {filteredProjects.length > 0 ? (
                        <Table
                            key={`${projectFilter}-${projectKindFilter}-${registrantFilter.join(',')}`}
                            rowKey="id"
                            columns={this.getColumns()}
                            dataSource={filteredProjects}
                            pagination={{
                                pageSize: 20,
                                showSizeChanger: false,
                                showQuickJumper: true,
                                current: this.state.listPage,
                                showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条 / 共 ${total} 条`,
                                onChange: page => this.setState({ listPage: page }),
                            }}
                            scroll={{ x: 2520 }}
                        />
                    ) : (
                        <Empty description={this.getEmptyDescription(projectFilter, projectKindFilter)} />
                    )}
                </Spin>
                {this.renderBatchApplyModal()}
                {this.renderApplyPermissionModal()}
                {this.renderAuthorizationModal()}
                {this.renderUploadAuthorizerModal()}
                {this.renderPermissionRequestModal()}
                    <VibeProjectDeleteDialog
                        visible={this.state.deleteVisible}
                        project={this.state.deleteProject}
                        onClose={this.closeProjectDelete}
                        onDeleted={this.loadProjects}
                    />
                <VibeCodexGuideDialog
                    visible={this.state.codexGuideVisible}
                    scene={this.state.codexGuideScene}
                    onClose={() => this.setState({
                        codexGuideVisible: false,
                        codexGuideScene: VIBE_CODEX_GUIDE_SCENE.NORMAL,
                    })}
                />
            </div>
        )
    }
}

export default withRouter(VibeProjectList)
